import { useCallback, useEffect, useRef } from 'react';

// Idle-logout primitive. After `timeoutMs` of no user input it calls
// `onTimeout`; `warningMs` before that it calls `onWarn` so the UI can offer a
// "stay signed in" prompt. "Activity" = mouse / pointer / touch / keyboard /
// scroll. Writes to the activity timestamp are throttled so a moving mouse
// doesn't peg performance, and a 5-second poll checks the elapsed time.
//
// Each tab tracks its own activity (deliberate — keeps the wiring simple).

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'pointermove',
  'wheel',
];

// Don't trip the activity timestamp more than once per second.
const THROTTLE_MS = 1000;

interface UseIdleTimeoutOptions {
  timeoutMs: number;
  warningMs: number;
  onWarn: () => void;
  onTimeout: () => void;
  enabled?: boolean;
}

export function useIdleTimeout({
  timeoutMs,
  warningMs,
  onWarn,
  onTimeout,
  enabled = true,
}: UseIdleTimeoutOptions) {
  const lastActivityRef = useRef<number>(Date.now());
  const lastWriteRef = useRef<number>(0);
  const warnFiredRef = useRef<boolean>(false);
  const timedOutRef = useRef<boolean>(false);

  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();
    lastWriteRef.current = Date.now();
    warnFiredRef.current = false;
    timedOutRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onActivity = () => {
      const now = Date.now();
      // Throttle — don't update on every pixel of mouse movement.
      if (now - lastWriteRef.current < THROTTLE_MS) return;
      lastActivityRef.current = now;
      lastWriteRef.current = now;
      // Movement after a warning means the user is back — clear the warn flag.
      warnFiredRef.current = false;
    };

    for (const ev of ACTIVITY_EVENTS) {
      document.addEventListener(ev, onActivity, { passive: true });
    }

    const poll = window.setInterval(() => {
      if (timedOutRef.current) return;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= timeoutMs) {
        timedOutRef.current = true;
        onTimeout();
        return;
      }
      if (!warnFiredRef.current && elapsed >= timeoutMs - warningMs) {
        warnFiredRef.current = true;
        onWarn();
      }
    }, 5000);

    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        document.removeEventListener(ev, onActivity);
      }
      window.clearInterval(poll);
    };
  }, [enabled, timeoutMs, warningMs, onWarn, onTimeout]);

  return { reset };
}
